/**
 * File System Access API utilities for saving files with native save dialog
 * Provides fallback for browsers that don't support the API
 */

// Type definitions for File System Access API
interface FilePickerAcceptType {
  description?: string;
  accept: Record<string, string[]>;
}

interface SaveFileOptions {
  suggestedName: string;
  types?: FilePickerAcceptType[];
}

/**
 * Shows a native save file dialog and saves the content to the selected location
 * Falls back to traditional download if File System Access API is not supported
 * 
 * @param content - The content to save (string or Blob)
 * @param options - Save options including suggested filename and file types
 * @returns Promise that resolves when file is saved
 */
export async function saveFileWithDialog(
  content: string | Blob,
  options: SaveFileOptions
): Promise<void> {
  // Check if File System Access API is supported
  if ('showSaveFilePicker' in window) {
    try {
      // Show native save dialog
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: options.suggestedName,
        types: options.types,
      });

      // Create a writable stream
      const writable = await handle.createWritable();

      // Write the content
      if (typeof content === 'string') {
        await writable.write(content);
      } else {
        await writable.write(content);
      }

      // Close the file
      await writable.close();

      console.log('File saved successfully:', options.suggestedName);
    } catch (error: any) {
      // User cancelled or error occurred
      if (error.name === 'AbortError') {
        console.log('User cancelled save dialog');
        return;
      }
      console.error('Error saving file:', error);
      // Fall back to traditional download
      fallbackDownload(content, options.suggestedName);
    }
  } else {
    // Browser doesn't support File System Access API, use fallback
    console.log('File System Access API not supported, using fallback');
    fallbackDownload(content, options.suggestedName);
  }
}

/**
 * Fallback download method for browsers that don't support File System Access API
 * This is the traditional "download to downloads folder" approach
 */
function fallbackDownload(content: string | Blob, filename: string): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Saves an XLSX workbook with native save dialog
 * Works with the xlsx library's WorkBook type
 * 
 * @param XLSX - The XLSX library instance
 * @param workbook - XLSX WorkBook object
 * @param filename - Suggested filename
 * @returns Promise that resolves when file is saved
 */
export async function saveXLSXWithDialog(
  XLSX: any,
  workbook: any,
  filename: string
): Promise<void> {
  // Convert workbook to binary buffer
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });

  // Use the save dialog with the blob
  await saveFileWithDialog(blob, {
    suggestedName: filename,
    types: FileTypes.CSV
  });
}

/**
 * Creates file picker accept types for common file formats
 */
export const FileTypes = {
  JSON: [{
    description: 'JSON Files',
    accept: { 'application/json': ['.json'] },
  }],
  SQL: [{
    description: 'SQL Files',
    accept: { 'text/plain': ['.sql'] },
  }],
  CSHARP: [{
    description: 'C# Files',
    accept: { 'text/plain': ['.cs'] },
  }],
  CSV: [{
    description: 'Excel Files',
    accept: { 
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
  }],
};
